import { SyntheticEvent, useCallback } from 'react'
import { useRouter } from 'next/router'
import Trans from 'next-translate/Trans'
import { useFeaturesData, useUserData } from '@app/data'
import { metaSetter } from '@app/utils'
import { useWebsiteContext } from '@app/components/providers/website'
import { Header2 } from '@app/components/general/header'
import { UserManager } from '@app/managers'
import { useAuthContext } from '@app/components/providers/auth'
import { NotificationSettings } from '@app/components/settings/notifications'
import { HistoryView } from '@app/components/settings/history-view'
import { RemoveDataView } from '@app/components/settings/remove-data-view'
import { AnalyticsSettingsView } from '@app/components/settings/analytics-view'
import { useInteractiveContext } from '@app/components/providers/interactive'
import { ThemesView } from '@app/components/settings/themes-view'
import { LazyMount } from '@app/components/lazy/lazymount'
import { fetcher } from '@app/utils/fetcher'
import { BottomMenu } from '@app/components/general/navigation/bottom'
import { useTranslation } from '@app/lib/hooks/useTranslation'
import { LanguageView } from '@app/components/settings/language-view'
import { AuthContainer } from '@app/components/containers/auth-container'
import { AuthRequired } from '@app/components/empty/auth-required'
import { DatalessView } from '@app/components/settings/dataless-view'
import { DisableAIView } from '@app/components/settings/disable-ai-view'
import { P } from '@app/components/stateless/typo/p'
import { Link } from '@app/components/stateless/typo/link'
import { LinearBottom, SnackBar } from '@app/components/general'
import { AppLayout } from '@app/components/app-layout'
import loadNamespaces from 'next-translate/loadNamespaces'
import type { GetStaticProps } from 'next'
import { I18nDictionary } from 'next-translate'
import { EnableImagesView } from '@app/components/settings/enable-images-view'
import { EnablePDFView } from '@app/components/settings/enable-pdf-view'
import { useToast } from '@app/@/components/ui/use-toast'
import { Toaster } from '@app/@/components/ui/toaster'
import { STRIPE_KEY } from '@app/configs'

export const getStaticProps: GetStaticProps<{
  __lang: string
  __namespaces?: Record<string, I18nDictionary>
}> = async (ctx) => {
  return {
    props: await loadNamespaces({
      ...ctx,
      pathname: '/settings',
    }),
  }
}

function Settings() {
  const { t } = useTranslation('settings')
  const router = useRouter()
  const { toast } = useToast()

  const {
    settings: data, // user
    loading,
    filterEmailDatesData: filterEmailDates,
    onFilterEmailDates,
    updateUser,
    updateUserData,
    refetchSettings,
  } = useUserData(true, 'settings')

  const { toggleAlert } = useFeaturesData()
  const { account, setAccountType } = useAuthContext()
  const { removeWebsite } = useWebsiteContext()
  const { setSelectedWebsite } = useInteractiveContext()
  const { alertEnabled, activeSubscription } = account

  const user = data?.user
  const updatedUser = updateUserData?.updateUser?.user

  const filterEmailDatesData = filterEmailDates ?? user?.emailFilteredDates

  const storageless = updatedUser?.storageless ?? !!user?.storageless

  const aiDisabled = updatedUser?.aiDisabled ?? !!user?.aiDisabled

  const analyticsEnabled =
    updatedUser?.analyticsEnabled ?? !!user?.analyticsEnabled

  const imagesEnabled = updatedUser?.imagesEnabled ?? !!user?.imagesEnabled
  const pdfEnabled = updatedUser?.pdfEnabled ?? !!user?.pdfEnabled

  const webemoveText = t('confirm-website-remove')
  const accRemoveText = t('confirm-acc-remove')

  const onRemoveAllWebsitePress = useCallback(
    async (e?: SyntheticEvent<HTMLInputElement>) => {
      if (e && typeof e?.preventDefault === 'function') {
        e?.preventDefault()
      }
      if (window.confirm(webemoveText)) {
        try {
          await removeWebsite({
            variables: {
              url: '',
              deleteMany: true,
            },
          })
          setSelectedWebsite('')
        } catch (e) {
          console.error(e)
        }
      }
    },
    [removeWebsite, setSelectedWebsite, webemoveText]
  )

  const onDeleteAccountPress = useCallback(
    async (e?: SyntheticEvent<HTMLInputElement>) => {
      if (e && typeof e?.preventDefault === 'function') {
        e?.preventDefault()
      }
      if (window.confirm(accRemoveText)) {
        try {
          await fetcher('/user', null, 'DELETE')
          UserManager.clearUser()
          router.push('/')
        } catch (e) {
          console.error(e)
        }
      }
    },
    [router, accRemoveText]
  )

  const onAlertToggle = useCallback(
    async (e?: SyntheticEvent<HTMLInputElement>) => {
      if (e && typeof e?.preventDefault === 'function') {
        e?.preventDefault()
        e?.stopPropagation()
      }

      const nextValue = !alertEnabled

      setAccountType({
        authed: account?.authed,
        activeSubscription,
        alertEnabled: nextValue,
        analyticsDisabled: account?.analyticsDisabled,
        token: account?.token,
        pdfEnabled: account?.pdfEnabled,
        profileVisible: account?.profileVisible,
        role: account?.role,
        inited: true,
      })

      // todo: remove local storage usage for network + memory
      UserManager.setAlertsEnabled(nextValue)

      try {
        await toggleAlert({
          variables: {
            alertEnabled: nextValue,
          },
        })
      } catch (e) {
        console.error(e)
      }
    },
    [setAccountType, account, activeSubscription, alertEnabled, toggleAlert]
  )

  const onChangeLanguage = useCallback(
    async (language: string) => {
      try {
        await updateUser({
          variables: {
            language,
          },
        })
      } catch (e) {
        console.error(e)
      }
    },
    [updateUser]
  )

  const onAnalyticsPayEvent = useCallback(
    async (e: SyntheticEvent<HTMLButtonElement>) => {
      if (e && typeof e?.preventDefault === 'function') {
        e?.preventDefault()
      }
      if (!activeSubscription) {
        toast({
          title: (
            <Trans
              i18nKey={'errors:upgrade'}
              components={[
                <P key={'errors-g'} />,
                <Link
                  href={'/payments'}
                  key={'errors-p'}
                  className={'text-blue-600 hover:text-blue-500'}
                />,
              ]}
            />
          ),
        })
        return
      }

      const nextValue = analyticsEnabled

      try {
        await updateUser({
          variables: {
            // analyticsDisabled - should be analyticsEnabled
            analyticsEnabled: !nextValue,
          },
        })

        setAccountType({ ...account, analyticsDisabled: nextValue })
        UserManager.setAnalyticsVisibility(nextValue)
      } catch (e) {
        console.error(e)
      }
    },
    [
      account,
      updateUser,
      analyticsEnabled,
      activeSubscription,
      setAccountType,
      toast,
    ]
  )

  const onDataLessEvent = useCallback(
    async (e: SyntheticEvent<HTMLButtonElement>) => {
      if (e && typeof e?.preventDefault === 'function') {
        e?.preventDefault()
      }
      try {
        await updateUser({
          variables: {
            storageless: !storageless,
          },
        })
        await refetchSettings()
      } catch (e) {
        console.error(e)
      }
    },
    [updateUser, storageless, refetchSettings]
  )

  const onDisableAI = useCallback(
    async (e: SyntheticEvent<HTMLButtonElement>) => {
      if (e && typeof e?.preventDefault === 'function') {
        e?.preventDefault()
      }
      try {
        await updateUser({
          variables: {
            aiDisabled: !aiDisabled,
          },
        })
        await refetchSettings()
      } catch (e) {
        console.error(e)
      }
    },
    [updateUser, aiDisabled, refetchSettings]
  )

  const onImagesEnable = useCallback(
    async (e: SyntheticEvent<HTMLButtonElement>) => {
      if (e && typeof e?.preventDefault === 'function') {
        e?.preventDefault()
      }
      try {
        await updateUser({
          variables: {
            imagesEnabled: !imagesEnabled,
          },
        })
        await refetchSettings()
      } catch (e) {
        console.error(e)
      }
    },
    [updateUser, imagesEnabled, refetchSettings]
  )

  const onPdfEnable = useCallback(
    async (e: SyntheticEvent<HTMLButtonElement>) => {
      if (e && typeof e?.preventDefault === 'function') {
        e?.preventDefault()
      }

      const nextValue = !pdfEnabled

      try {
        await updateUser({
          variables: {
            pdfEnabled: nextValue,
          },
        })
        await refetchSettings()

        // @ts-ignore
        setAccountType((acc) => {
          return {
            ...acc,
            pdfEnabled: nextValue,
          }
        })

        UserManager.setPdfEnabled(nextValue)
      } catch (e) {
        console.error(e)
      }
    },
    [updateUser, refetchSettings, setAccountType, pdfEnabled]
  )

  if (!data && !loading) {
    return (
      <AuthContainer
        translation='settings'
        pageSubtitle='header'
        pageTitle='settings'
        user={account}
      >
        <AuthRequired />
      </AuthContainer>
    )
  }

  return (
    <AuthContainer
      translation='settings'
      pageSubtitle='header'
      pageTitle='settings'
      user={account}
    >
      <Header2 className='sr-only'>{t('header-sub')}</Header2>
      <div className='flex flex-col gap-y-6 py-6'>
        <NotificationSettings
          alertEnabled={alertEnabled}
          onAlertToggle={onAlertToggle}
          filterEmailDatesData={filterEmailDatesData}
          onFilterEmailDates={onFilterEmailDates}
          defaultDayTime={user?.emailMorningOnly}
          user={user}
          loading={loading}
        />
        <LazyMount
          onlyInnerClass
          className='max-h-[358px] md:max-h-[285px] bg-gray-300 dark:bg-gray-700'
        >
          <ThemesView user={account} loading={loading} />
        </LazyMount>
        <AnalyticsSettingsView
          user={user}
          loading={loading}
          onSubmit={onAnalyticsPayEvent}
          analyticsEnabled={analyticsEnabled}
        />
        <DisableAIView
          onDisableAI={onDisableAI}
          aiDisabled={aiDisabled}
          user={account}
          loading={loading}
        />
        <EnableImagesView
          onEnableImages={onImagesEnable}
          imagesEnabled={imagesEnabled}
          user={account}
          loading={loading}
        />
        {STRIPE_KEY ? (
          <EnablePDFView
            onEnablepdf={onPdfEnable}
            pdfEnabled={pdfEnabled}
            user={account}
            loading={loading}
          />
        ) : null}
        {[11, 20, 0].includes(user?.role || 0) ? null : (
          <DatalessView
            onDataLessEvent={onDataLessEvent}
            storageless={storageless}
            user={account}
            loading={loading}
          />
        )}
        <LanguageView
          onChangeLanguage={onChangeLanguage}
          user={account}
          loading={loading}
        />
        <LazyMount>
          <HistoryView user={account} loading={loading} />
        </LazyMount>
        <RemoveDataView
          onRemoveAllWebsitePress={onRemoveAllWebsitePress}
          onDeleteAccountPress={onDeleteAccountPress}
          user={account}
          loading={loading}
        />
      </div>
      <BottomMenu active={'settings'} />
      <LinearBottom loading={loading} />
      <SnackBar topLevel />
      <Toaster />
    </AuthContainer>
  )
}

Settings.getLayout = AppLayout

export default metaSetter(
  { Settings },
  {
    gql: true,
    translation: 'settings',
  }
)
